import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useNavigate, useParams } from "#routers/hooks";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import {
  EditUserFormPanel,
  UserUpdateFormType,
} from "#src/batteries-included-components/Panels/DetailsPanel/EditUserFormPanel";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
  useIsOpsHubExperience,
} from "#src/contexts/AuthenticatedContext.helpers";
import { USERS_LIST_BREADCRUMB } from "#src/routes/settings/users";
import {
  USER_DETAILS_BREADCRUMB,
  linkToUserDetailPage,
} from "#src/routes/settings/users/detail";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  FormButton,
  Page,
  useForm,
  usePrevious,
  useToast,
} from "@validereinc/common-components";
import {
  UpdateUserInput,
  UserTypeEnum,
  UsersAdapter,
} from "@validereinc/domain";
import React, { useContext, useEffect } from "react";
import { EDIT_USER_PROFILE_BREADCRUMB } from ".";
import { SETTINGS_BREADCRUMB } from "../..";
import {
  UserDetailContext,
  UserDetailProvider,
} from "../detail/UserDetailContext";
import { isEqual } from "es-toolkit/compat";

const EditUserPageContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userDetails } = useContext(UserDetailContext) ?? {};
  const form = useForm<UserUpdateFormType>({ defaultValues: {} });
  const queryClient = useQueryClient();
  const userData = userDetails?.data;
  const previousUserData = usePrevious(userData);

  useEffect(() => {
    if (userData && !isEqual(userData, previousUserData)) {
      const isMachine = userData.type === UserTypeEnum.machine;
      form.reset({
        type: userData.type,
        name: userData.name,
        timezone: userData.timezone,
        status: userData.status,
        ...(isMachine
          ? {}
          : {
              email: "email" in userData ? userData.email : undefined,
              phone:
                "phone" in userData
                  ? userData.phone
                    ? userData.phone
                    : null
                  : undefined,
              can_access_quicksight_dashboards:
                "can_access_quicksight_dashboards" in userData
                  ? userData.can_access_quicksight_dashboards
                  : undefined,
              can_access_quicksight_console:
                "can_access_quicksight_console" in userData
                  ? userData.can_access_quicksight_console
                  : undefined,
            }),
      });
    }
  }, [userData]);

  const breadcrumbs = useBreadcrumbs(
    [
      SETTINGS_BREADCRUMB,
      USERS_LIST_BREADCRUMB,
      USER_DETAILS_BREADCRUMB,
      EDIT_USER_PROFILE_BREADCRUMB,
    ],
    {
      2: userDetails?.data?.name,
    },
    { userId: userDetails?.data?.id ?? "" }
  );

  const navigateToUserDetail = () => {
    if (!userDetails?.data?.id) return;

    navigate({ pathname: linkToUserDetailPage(userDetails.data?.id) });
  };

  const updateUser = useMutation({
    mutationFn: (values: UserUpdateFormType) => {
      if (!userDetails?.data?.id) {
        throw new Error("A user must be specified to update");
      }

      const isMachine = userDetails.data.type === UserTypeEnum.machine;

      const updateBody: UpdateUserInput = isMachine
        ? {
            name: values.name,
            timezone: values.timezone,
          }
        : {
            name: values.name,
            phone: values.phone ? values.phone : null,
            timezone: values.timezone,
            can_access_quicksight_dashboards:
              values.can_access_quicksight_dashboards,
            can_access_quicksight_console: values.can_access_quicksight_console,
          };

      return UsersAdapter.updateOne({
        id: userDetails.data.id,
        data: updateBody,
      });
    },
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      toast.push({
        intent: "success",
        description: `Successfully updated account for ${updatedUser.data?.name}`,
      });
      navigateToUserDetail();
    },
    onError: (err, mutationData) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: `Failed to update account for ${mutationData.name}`,
      });
    },
  });

  const footer = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 16,
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      <Button
        onClick={navigateToUserDetail}
        disabled={userDetails?.isLoading || updateUser.isLoading}
      >
        Cancel
      </Button>

      <FormButton
        variant="primary"
        type="submit"
        onClick={form.handleSubmit(updateUser.mutate as any)}
        disabled={!!userDetails?.isLoading || updateUser.isLoading}
      >
        Save
      </FormButton>
    </div>
  );

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(updateUser.mutate as any) as any}
      style={{ height: "100%" }}
    >
      <Page
        title="Edit User"
        category={`User | ${userDetails?.data?.name}`}
        isLoading={userDetails?.isLoading}
        breadcrumbs={breadcrumbs}
        footer={footer}
      >
        <EditUserFormPanel />
      </Page>
    </Form>
  );
};

export const EditUserPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const {
    claims: {
      user: { id: currentUserId },
    },
  } = useAuthenticatedContext();
  const [isPageAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:roles_permissions",
    permissionQuery: "users:write",
  });
  const [isOpsExperienceEnabled] = useIsOpsHubExperience();

  const isThisUserCurrentUser = currentUserId === userId;

  if (!isThisUserCurrentUser && (!isPageAvailable || isOpsExperienceEnabled)) {
    return <AccessDeniedLayout />;
  }

  return (
    <UserDetailProvider userId={userId}>
      <EditUserPageContent />
    </UserDetailProvider>
  );
};
